/** Static policy, our-story, FAQ, 404 pages, plus Wishlist and Account. */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ShopNavbar from '../components/ShopNavbar';
import ShopFooter from '../components/ShopFooter';
import { shopApi, useShop, INR } from '../lib/shop';
import ProductCard from '../components/ProductCard';
import '../styles/shop.css';

export const Page = ({ eyebrow, title, children }) => (
  <div className="si-page">
    <ShopNavbar />
    <div className="max-w-4xl mx-auto px-3 md:px-6 py-4 md:py-6">
      <div className="si-card p-6 md:p-10" data-testid="static-page">
        <p className="text-sm font-bold text-[var(--si-orange)] uppercase mb-2">{eyebrow}</p>
        <h1 className="text-3xl md:text-4xl font-bold mb-6 text-[var(--si-black)]">{title}</h1>
        <div className="max-w-none text-[15px] md:text-base text-[var(--si-black)] leading-relaxed space-y-4 [&_h3]:text-lg [&_h3]:font-bold [&_h3]:pt-2">
          {children}
        </div>
      </div>
    </div>
    <ShopFooter />
  </div>
);

export const SizeGuide = () => (
  <Page eyebrow="Size Guide" title="Finding your fit.">
    <p>Measurements are in centimetres. Our ready-to-wear uses a relaxed modern cut — if you're between sizes we recommend sizing down for a tailored look and up for an oversized feel.</p>
    <table className="w-full border-collapse"><thead><tr className="border-b border-[#C7CCD6]"><th className="text-left py-2">Size</th><th>Chest</th><th>Waist</th><th>Hip</th></tr></thead>
      <tbody className="text-sm">
        <tr className="border-b border-[var(--si-line)]"><td className="py-2">XS</td><td className="text-center">86</td><td className="text-center">70</td><td className="text-center">88</td></tr>
        <tr className="border-b border-[var(--si-line)]"><td className="py-2">S</td><td className="text-center">92</td><td className="text-center">76</td><td className="text-center">94</td></tr>
        <tr className="border-b border-[var(--si-line)]"><td className="py-2">M</td><td className="text-center">98</td><td className="text-center">82</td><td className="text-center">100</td></tr>
        <tr className="border-b border-[var(--si-line)]"><td className="py-2">L</td><td className="text-center">104</td><td className="text-center">88</td><td className="text-center">106</td></tr>
        <tr className="border-b border-[var(--si-line)]"><td className="py-2">XL</td><td className="text-center">110</td><td className="text-center">94</td><td className="text-center">112</td></tr>
      </tbody>
    </table>
  </Page>
);

export const Contact = () => (
  <Page eyebrow="Contact" title="Say hello.">
    <p>We'd love to hear from you — a question, a design idea, or just a note.</p>
    <p>Email: <strong>info@scholarindustry.com</strong><br/>Hours: Monday–Saturday, 10:00–19:00 IST</p>
  </Page>
);

export const NotFound = () => (
  <Page eyebrow="404" title="This page has slipped out of circulation.">
    <p>The piece you're looking for may have moved, or it's a page we haven't written yet.</p>
    <Link to="/" className="text-[var(--si-blue)] font-semibold underline">Return to the home page</Link>
  </Page>
);

export const Wishlist = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { refreshWishlist } = useShop();
  useEffect(() => {
    if (!localStorage.getItem('token')) { setLoading(false); return; }
    shopApi.getWishlist().then((d) => { setProducts(d.products || []); setLoading(false); });
  }, []);
  const toggle = async (slug) => {
    await shopApi.toggleWishlist(slug);
    setProducts((p) => p.filter((x) => x.slug !== slug));
    refreshWishlist();
  };
  return (
    <div className="si-page">
      <ShopNavbar />
      <div className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-6">
        <div className="si-card p-4 md:p-6" data-testid="wishlist-page">
          <h1 className="text-xl md:text-2xl font-bold mb-4">My Wishlist {products.length > 0 && `(${products.length})`}</h1>
          {!localStorage.getItem('token') ? (
            <p className="py-10 text-center">Please <Link to="/login" className="text-[var(--si-blue)] font-semibold underline">log in</Link> to see your wishlist.</p>
          ) : loading ? <p className="py-10 text-center">Loading…</p> : products.length === 0 ? (
            <p className="py-10 text-center" data-testid="wishlist-empty">Your wishlist is empty. <Link to="/shop" className="text-[var(--si-blue)] font-semibold underline">Start shopping</Link></p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3" data-testid="wishlist-grid">
              {products.map((p) => (
                <div key={p.slug}>
                  <ProductCard p={p} />
                  <button onClick={() => toggle(p.slug)} className="mt-1 ml-3 text-sm font-semibold text-[#DC2626] hover:underline" data-testid={`wishlist-remove-${p.slug}`}>Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <ShopFooter />
    </div>
  );
};

export const Account = () => {
  const [orders, setOrders] = useState([]);
  const [user, setUser] = useState(null);
  useEffect(() => {
    try { setUser(JSON.parse(localStorage.getItem('user'))); } catch { /* noop */ }
    if (localStorage.getItem('token')) shopApi.myOrders().then((d) => setOrders(d.orders || []));
  }, []);
  const logout = () => { localStorage.clear(); window.location.href = '/'; };
  if (!localStorage.getItem('token')) {
    return <Page eyebrow="Account" title="Log in to your account"><p><Link to="/login" className="si-btn si-btn-primary" data-testid="account-login-link">Login / Sign up</Link></p></Page>;
  }
  return (
    <div className="si-page">
      <ShopNavbar />
      <div className="max-w-5xl mx-auto px-3 md:px-6 py-4 md:py-6 space-y-4">
        <div className="si-card p-5 md:p-6 flex flex-wrap items-center justify-between gap-4" data-testid="account-header">
          <div>
            <p className="text-sm text-[var(--si-grey)]">Hello,</p>
            <h1 className="text-2xl font-bold">{user?.name || 'Welcome back'}</h1>
            <p className="text-sm text-[var(--si-grey)]">{user?.email}</p>
          </div>
          <div className="flex gap-3">
            <button onClick={logout} className="si-btn si-btn-outline" data-testid="account-logout">Log out</button>
          </div>
        </div>
        <div className="si-card">
          <h2 className="px-5 py-3 border-b border-[var(--si-line)] text-lg font-bold">My Orders</h2>
          {orders.length === 0 ? <p className="p-6 text-[var(--si-grey)]" data-testid="account-no-orders">No orders yet. <Link to="/shop" className="text-[var(--si-blue)] font-semibold">Start shopping</Link></p> : (
            <ul className="divide-y divide-[var(--si-line)]" data-testid="account-orders">
              {orders.map((o) => (
                <li key={o.id}>
                  <Link to={`/order/${o.id}`} className="px-5 py-4 flex items-center justify-between gap-3 hover:bg-[var(--si-bg-soft)]" data-testid={`account-order-${o.id}`}>
                    <div>
                      <p className="font-semibold">Order #{o.id.slice(0, 8)}</p>
                      <p className="text-sm text-[var(--si-grey)]">{(o.created_at || '').slice(0, 10)} · {o.items?.length} item(s) · <span className="capitalize font-medium text-[var(--si-black)]">{(o.status || '').replaceAll('_', ' ')}</span></p>
                    </div>
                    <p className="font-bold">{INR(o.total)}</p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <ShopFooter />
    </div>
  );
};
